import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { ChatMessage, ChatSendRequest, ChatThread } from '@/types/chat';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/**
 * Phase 07 chat — three hooks:
 *
 * - `useChatThreads(entityType, entityId)` — list of threads on an
 *   entity, with `refetch` for invalidation.
 * - `useChatMessages(threadId)` — paginated message list with a 10s
 *   polling refresh. Email is the delivery backbone; in-platform
 *   message updates catch up on the next poll.
 * - `useSendChatMessage(threadId)` — mutator that calls the backend;
 *   callers can `refetch()` after sending for immediate feedback.
 */

const CHAT_POLL_INTERVAL_MS = 10_000;

export interface UseChatThreadsResult {
  threads: ChatThread[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useChatThreads(
  entityType: 'enquiry' | 'report' | 'report_request' | undefined,
  entityId: string | undefined
): UseChatThreadsResult {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!entityType || !entityId) {
      setThreads([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.listChatThreads({ entityType, entityId });
      setThreads(result);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return { threads, isLoading, error, refetch: fetchOnce };
}

export interface UseChatMessagesResult {
  messages: ChatMessage[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const EMPTY_MESSAGES: ChatMessage[] = [];

interface MessagesSnapshot {
  /** The thread the rows belong to, so a stale list never renders under
   * a different thread while its first fetch is in flight. */
  threadId: string;
  messages: ChatMessage[];
}

export function useChatMessages(
  threadId: string | undefined,
  /** Admin merged view — interleave the internal sibling's notes. */
  includeInternal = false
): UseChatMessagesResult {
  const poll = usePoll<MessagesSnapshot>({
    enabled: threadId !== undefined,
    interval: CHAT_POLL_INTERVAL_MS,
    refetchDeps: [threadId, includeInternal],
    queryFn: async ({ signal, etag, manual }) => {
      if (!threadId) {
        throw new Error('threadId is required');
      }
      // Scheduled ticks poll on the stored token — a quiet thread is a
      // body-less 304; manual reads are always full.
      const result = await vnextApiClient.listChatMessages(
        threadId,
        { limit: 50, includeInternal },
        { signal, etag: manual ? null : etag }
      );
      if (result === 'not-modified') {
        return UNCHANGED;
      }
      // Backend returns reverse-chronological; the UI wants oldest-first
      // for natural reading order, so flip.
      return withEtag({ threadId, messages: [...result.data].reverse() }, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  const { refetch: pollRefetch } = poll;
  const refetch = useCallback(
    () => (threadId ? pollRefetch() : Promise.resolve()),
    [threadId, pollRefetch]
  );

  const messages =
    threadId !== undefined && poll.data?.threadId === threadId
      ? poll.data.messages
      : EMPTY_MESSAGES;

  return {
    messages,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch,
  };
}

export interface UseSendChatMessageResult {
  /** Sends to the hook's thread — or to `targetThreadId` when the thread
   * was created a moment ago and the threads list hasn't caught up. */
  send: (body: ChatSendRequest, targetThreadId?: string) => Promise<ChatMessage>;
  isSending: boolean;
  error: Error | null;
}

export function useSendChatMessage(threadId: string | undefined): UseSendChatMessageResult {
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const send = useCallback(
    async (body: ChatSendRequest, targetThreadId?: string) => {
      const target = targetThreadId ?? threadId;
      if (!target) {
        throw new Error('threadId is required');
      }
      setIsSending(true);
      setError(null);
      try {
        return await vnextApiClient.sendChatMessage(target, body);
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        setError(e);
        throw e;
      } finally {
        setIsSending(false);
      }
    },
    [threadId]
  );

  return { send, isSending, error };
}

export type { ChatThreadKind } from '@/types/chat';
